import React, { useState, useContext, useEffect, useRef, useCallback, useMemo } from "react";
import { NavLink } from "react-router-dom";
import { useReactToPrint } from "react-to-print";
import StockItemPrint from "../../reports/StockItemPrint";
import { BiRefresh } from "react-icons/bi";
import Select from "react-select";

//pages & includes
import ManageSidebar from "../ManageSidebar";

//functions
import {
  _t,
  getCookie,
  formatPrice,
  currencySymbolLeft,
  currencySymbolRight,
  customStyle,
  checkPermission,
  getSystemSettings,
} from "../../../../../functions/Functions";
import { useTranslation } from "react-i18next";
import { MdDelete } from 'react-icons/md';
import { FaCheckCircle } from "react-icons/fa";

//axios and base url
import axios from "axios";
import { BASE_URL } from "../../../../../BaseUrl";

//3rd party packages
import { Helmet } from "react-helmet";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import { ToastError, ToastSuccess } from "../../../../../functions/ToastShow";
import Moment from "react-moment";

//context consumer
import { RestaurantContext } from "../../../../../contexts/Restaurant";
import { UserContext } from "../../../../../contexts/User";
import { SettingsContext } from "../../../../../contexts/Settings";
import DataTable from "../../../../common/DataTable";
import CsvImportExport from "../../../../../components/CsvImportExport";

const PurchaseOrderHistory = () => {
  const { t } = useTranslation();

  let {
    //common
    loading,
    setLoading,

    //purchase order
    getPaginatedPurchaseOrder,
    setPurchaseOrderList,
    setPurchaseOrderListForSearch,
    purchaseOrderListForSearch,
    purchaseOrderList,
    branchForListSearch,
    //pagination
    dataPaginating,
    setDataPaginating
  } = useContext(RestaurantContext);

  let { authUserInfo, getSupplier, supplierForSearch } = useContext(UserContext);
  const { generalSettings } = useContext(SettingsContext);

  //search result
  let [searchedSupplier, setSearchedSupplier] = useState({
    list: null,
    searched: false,
  });

  const [btnDisable, setBtnDisable] = useState("");

  const searchRef = useRef(null);
  //useEffect == componentDidMount
  useEffect(() => {

    getPaginatedPurchaseOrder(1)

    if (!supplierForSearch) {
      getSupplier();
    }
  }, []);
  //search purchase order here
  const handleSearch = (e) => {
    let searchInput = e.target.value.toLowerCase();
    if (searchInput.length === 0) {
      setSearchedSupplier({ ...searchedSupplier, searched: false });
    } else {
      let searchedList = purchaseOrderListForSearch.filter((item) => {
        //name
        let lowerCaseItemSupplierName = item.supplier_name && item.supplier_name.toLowerCase();
        //branch
        let lowerCaseBranch = item.branch_name.toLowerCase();
        //email
        let lowerCaseItemInvoice = item.document_identity !== null && item.document_identity.toLowerCase();

        return (
          (lowerCaseItemSupplierName && lowerCaseItemSupplierName.includes(searchInput)) ||
          lowerCaseItemInvoice.includes(searchInput) ||
          lowerCaseBranch.includes(searchInput)
        );
      });
      setSearchedSupplier({
        ...searchedSupplier,
        list: searchedList,
        searched: true,
      });
    }
  };

  const handleFetchData = (page) => {
    getPaginatedPurchaseOrder(page);
  };

  const getListData = useCallback(() => {
    if (searchedSupplier.searched) {
      return searchedSupplier.list;
    }
    return purchaseOrderList?.data || [];
  }, [searchedSupplier, purchaseOrderList]);

  const onClearSearch = () => {
    setSearchedSupplier({ ...searchedSupplier, searched: false });
    if (searchRef.current) {
      searchRef.current.value = "";
    }
  }

  const handleSetCsvResponse = useCallback((data) => {
    setPurchaseOrderList(data[0]);
    setPurchaseOrderListForSearch(data[1]);
    setSearchedSupplier({
      ...searchedSupplier,
      list: data[1],
    });
  }, [])

  //delete confirmation modal of purchase order
  const handleDeleteConfirmation = (id) => {
    confirmAlert({
      customUI: ({ onClose }) => {
        return (
          <div className="card card-body">
            <h1>{_t(t("Are you sure?"))}</h1>
            <p className="text-center">{_t(t("You want to delete this?"))}</p>
            <div className="d-flex justify-content-center">
              <button
                className="btn btn-primary"
                onClick={() => {
                  handleDeletePurchase(id);
                  onClose();
                }}
              >
                {_t(t("Yes, delete it!"))}
              </button>
              <button className="btn btn-success ml-2 px-3" onClick={onClose}>
                {_t(t("No"))}
              </button>
            </div>
          </div>
        );
      },
    });
  };

  //delete purchase here
  const handleDeletePurchase = (id) => {
    setLoading(true);
    const url = BASE_URL + `/settings/purchase-order/${id}`;
    return axios
      .delete(url, {
        headers: { Authorization: `Bearer ${getCookie()}` },
      })
      .then((res) => {
        handleSetCsvResponse(res.data)
        setLoading(false);
        ToastSuccess({ title: `${_t(t("Purchase has been deleted"))}` });
      }).catch(() => {
        setLoading(false);
        ToastError({ title: `${_t(t("Please try again"))}` });
      });
  };

  const handlePostApi = (id) => {
    // setLoading(true);
    setBtnDisable(id)
    const url = BASE_URL + `/settings/mark-is-post-po`;
    const formData = {
      id: id,
      status: 1
    }
    return axios
      .post(url, formData, {
        headers: { Authorization: `Bearer ${getCookie()}` },
      })
      .then((res) => {
        handleSetCsvResponse(res.data);
        setBtnDisable("")
        setLoading(false);
        ToastSuccess({ title: `${_t(t("Purchase has been post"))}` });
      }).catch((err) => {
        setLoading(false);
        setBtnDisable("")
        if (err.response.data?.status === 0) {
          ToastError({ title: err.response.data?.message });
          return
        }
        ToastError({ title: `${_t(t("Please try again"))}` });
      });
  }

  //print bills states and function
  const [newRow, setNewRow] = useState([]);
  let [puchaseOrderView, setPuchaseOrderView] = useState({
    date: null,
    doc_id: null,
    supplier: null,
    branch: '',
    remarks: '',
    total_amount: ''
  });

  const printColumns = useMemo(() => {
    const hasTax = newRow?.some((item) => Number(item?.tax || item?.tax_amount || 0) > 0 || (item?.tax !== undefined && item?.tax !== null && item?.tax !== ""));
    const hasDiscount = newRow?.some((item) => Number(item?.discount || 0) > 0);
    const hasTotal = newRow?.some((item) => item?.total !== undefined);

    const cols = [
      {
        key: "item_name",
        label: _t(t("Name")),
        accessor: "item_name",
        headerClassName: "table_child_ordertype",
      },
      {
        key: "qty",
        label: _t(t("Qty")),
        accessor: "qty",
      },
      {
        key: "unit_name",
        label: _t(t("Unit")),
        accessor: "unit_name",
      },
      {
        key: "rate",
        label: _t(t("Rate")),
        accessor: (row) => row?.rate ?? null,
        total: () => _t(t("Total")),
      },
    ];

    if (hasTax) {
      cols.push({
        key: "tax",
        label: _t(t("Tax")),
        accessor: (row) => (row?.tax !== undefined && row?.tax !== null ? formatPrice(row.tax) : (row?.tax_amount ? formatPrice(row.tax_amount) : "0.00")),
        total: (totals) => {
          const sumTax = newRow?.reduce((acc, item) => acc + Number(item?.tax || item?.tax_amount || 0), 0);
          if (sumTax > 0) {
            return sumTax.toFixed(2);
          }
          if (totals?.total_tax !== undefined && totals?.total_tax !== null && totals?.total_tax !== "" && Number(totals?.total_tax) > 0) {
            return parseFloat(totals.total_tax).toFixed(2);
          }
          return "0.00";
        },
      });
    }

    if (hasDiscount) {
      cols.push({
        key: "discount",
        label: _t(t("Discount")),
        accessor: (row) => formatPrice(row?.discount || 0),
        total: (totals) => {
          const sumDisc = newRow?.reduce((acc, item) => acc + Number(item?.discount || 0), 0);
          if (sumDisc > 0) {
            return sumDisc.toFixed(2);
          }
          if (totals?.total_discount !== undefined && totals?.total_discount !== null && totals?.total_discount !== "" && Number(totals?.total_discount) > 0) {
            return parseFloat(totals.total_discount).toFixed(2);
          }
          return "0.00";
        },
      });
    }

    cols.push({
      key: "amount",
      label: _t(t("Amount")),
      accessor: "amount",
      format: "price",
      total: (totals) =>
        totals?.total_amount
          ? parseFloat(totals.total_amount).toFixed(2)
          : "",
    });

    if (hasTotal) {
      cols.push({
        key: "total",
        label: _t(t("Total")),
        accessor: "total",
        format: "price",
        total: (totals) =>
          totals?.net_amount
            ? parseFloat(totals.net_amount).toFixed(2)
            : "",
      });
    }

    return cols;
  }, [newRow, t]);

  const componentRef = useRef();

  const handleOnlyPrint = useReactToPrint({
    content: () => componentRef.current,
    pageStyle: "@page { margin: 15px }"
  });

  const handleGetPrintData = async (id) => {
    const url = BASE_URL + "/settings/purchase-order/" + parseInt(id);
    setLoading(true)
    try {
      const res = await axios
        .get(url, {
          headers: { Authorization: `Bearer ${getCookie()}` },
        });
      if (res.data && res.data.purchase_order_detail) {
        let purchaseDate = new Date(res.data.document_date);
        let deliveryDate = new Date(res.data.delivery_date);
        let suplier = supplierForSearch?.find(item => item?.id === res.data.supplier_id);
        const branch = branchForListSearch[res.data.branch_id];
        const sumItemTax = res.data.purchase_order_detail.reduce((acc, item) => acc + Number(item?.tax || item?.tax_amount || 0), 0);
        const totalTax = sumItemTax > 0 ? sumItemTax : (res.data?.total_tax || res.data?.tax_amount || res.data?.tax || 0);
        const sumItemDisc = res.data.purchase_order_detail.reduce((acc, item) => acc + Number(item?.discount || 0), 0);
        const totalDiscount = sumItemDisc > 0 ? sumItemDisc : (res.data?.total_discount || res.data?.discount || 0);
        setPuchaseOrderView({
          ...puchaseOrderView,
          date: purchaseDate,
          delivery_date: deliveryDate,
          branch,
          supplier: suplier,
          doc_id: res.data.document_identity,
          ref_doc: res.data?.ref_document_identity,
          remarks: res.data.remarks,
          total_amount: res.data.total_amount,
          total_tax: totalTax,
          total_discount: totalDiscount,
          net_amount: res.data?.net_amount,
        })
        setNewRow([...res.data.purchase_order_detail]);
        setLoading(false)
        setTimeout(() => {
          handleOnlyPrint();
        }, 0);
      }
    } catch (error) {
      setLoading(false)
      console.log("error==>", error)
      ToastError({ title: `${_t(t("Please try again"))}` });
    }
  }

  // po close
  const handleClosingPo = async (id) => {
    const url = BASE_URL + "/settings/close-purchase-order/" + parseInt(id);
    // setDataPaginating(true)
    setBtnDisable(id)
    try {
      const res = await axios.get(url, {
          headers: { Authorization: `Bearer ${getCookie()}` },
        });
      if (res.data) {
        handleSetCsvResponse(res.data)
        setBtnDisable("")
        setDataPaginating(false);
        ToastSuccess({ title: `${_t(t("PO has been close"))}` });
      }
    } catch (error) {
      setDataPaginating(false);
      setBtnDisable("")
      if(error.response?.data?.message){
        ToastError({ title: `${_t(t(error.response.data.message))}` });
        return
      }
      ToastError({ title: `${_t(t("Please try again"))}` });
    }
  }

  const { canPoApprove, canPoPost } = useMemo(() => {
    const permissions = authUserInfo?.permissions || [];
  
    return {
      canPoApprove: checkPermission(permissions, "MS PO Approve") || (authUserInfo && authUserInfo.details?.user_type === "superAdmin"),
      canPoPost: checkPermission(permissions, "PO Post"),
    };
  }, [authUserInfo?.permissions]);

  const PoTypes = [...(canPoApprove ? [{name:"Approve"}] : []), ...(canPoPost ? [{name:"Post"}] : [])];

  const [multiPoPost, setMultiPoPost] = useState({
    poItems: null,
    type: PoTypes[0] ?? null,
    is_post: 1,
  });

  const handleSetType = (type)=>{
    setMultiPoPost((curr)=>({
      ...curr,
      poItems: null,
      type:type
    }))
  }

  const toggleSelectAll = (e) =>{
    e.stopPropagation();
    let updated = {...multiPoPost.poItems};
    if(e.target.checked){
      ((searchedSupplier?.searched && searchedSupplier?.list) || purchaseOrderList.data).forEach((item)=>{
        if(multiPoPost.type?.name === "Approve"){
          if(!item.is_post && item.status === "Pending"){
            updated[item.id] = item.id;
          }
        }else if(!item.is_post && item.status === "Approved"){
          updated[item.id] = item.id;
        }
      })
    }else{
      ((searchedSupplier?.searched && searchedSupplier?.list) || purchaseOrderList.data).forEach((item)=>{
        if(updated[item.id]){
          delete updated[item.id];
        }
      })
    }

    setMultiPoPost((curr)=>({
      ...curr,
      poItems: updated
    }));
  }
  
  const toggleSelect = (id) => {
    let updated = {...multiPoPost.poItems};
    if (updated[id]) {
      delete updated[id];
    } else {
      updated[id] = id;
    }
    setMultiPoPost((curr)=>({
      ...curr,
      poItems: updated
    }));
  };

  //multi post
  const handleSavemultiPoPost = async (e) => {
    e.preventDefault();
    if(!multiPoPost?.type){
      ToastError({ title: `${_t(t("Please select type or check permision"))}` });
      return false;
    }
    if(!multiPoPost?.poItems || !Object.values(multiPoPost?.poItems)?.length){
      ToastError({ title: `${_t(t("Please select item to post"))}` });
      return false;
    }
    setDataPaginating(true);

    const url = BASE_URL + "/settings/multi-post-purchase-order";
    const formData = {
      purchaseOrderId: multiPoPost.poItems
        ? Object.values(multiPoPost.poItems)
        : [],
      type: multiPoPost.type.name.toLowerCase(),
      is_post: multiPoPost.is_post ?? 1,
    };
    try {
      const res = await axios.post(url, formData, {
        headers: { Authorization: `Bearer ${getCookie()}` },
      });
      if (res.data) {
        setDataPaginating(false)
        setPurchaseOrderList(res.data[0])
        setPurchaseOrderListForSearch(res.data[1]);
        setSearchedSupplier({
          ...searchedSupplier,
          list: res.data[1],
        })
        setMultiPoPost({
          poItems: null,
          type: PoTypes[0],
          is_post: 1,
        })
        ToastSuccess({ title: `${_t(t("Cash Payment has been posted"))}` });
        if(document.getElementById("multi_post_check")){
          document.getElementById("multi_post_check").checked = false;
        }
      }
    } catch (error) {
      setDataPaginating(false);
      if (error?.response?.data && error.response.data?.status == 0) {
        ToastError({ title: `${_t(t(error.response.data.message))}` });
        return false;
      }
      ToastError({ title: `${_t(t("Please try again"))}` });
    }
  };

  //po approve
  const handleApprove = (id) => {
    // setDataPaginating(true);
    setBtnDisable(id)
    const url = BASE_URL + `/settings/update-po-status/${id}`;
    return axios
      .get(url, {
        headers: { Authorization: `Bearer ${getCookie()}` },
      })
      .then((res) => {
        handleSetCsvResponse(res.data);
        setBtnDisable("")
        setDataPaginating(false);
        ToastSuccess({ title: `${_t(t("PO has been approve"))}` });
      }).catch(() => {
        setDataPaginating(false);
        setBtnDisable("")
        ToastError({ title: `${_t(t("Please try again"))}` });
      });
  }

  const COLUMNS = [
    { label: "S/L", render: (item, i) => i + 1 },
    // ...((canPoApprove || canPoPost) ? [
    //     {labelRender:()=> {
    //     return(
    //       <div className="d-flex justify-content-center align-items-center">
    //         <input
    //           id="multi_post_check"
    //           type="checkbox"
    //           className="form-check-input table-text mr-1"
    //           onChange={(e) => {
    //             toggleSelectAll(e);
    //           }}
    //         />
    //         <label htmlFor="multi_post_check" className="mb-0 mr-2 mt-1">
    //           All 
    //         </label>

    //         <button title="post" className="btn btn-sm btn-dark" onClick={(e)=>handleSavemultiPoPost(e)}>
    //           <i className="fa fa-check-square-o "></i>
    //         </button>
    //       </div>
    //     )
    //     }, render: (item, i) => {
    //       return (
    //         <input
    //           disabled={item.is_post || (multiPoPost.type?.name === "Approve" ? (!item.is_post && item.status !== "Pending") : (!item.is_post  && item.status !== "Approved"))}
    //           id={item.id}
    //           type="checkbox"
    //           className="form-check-input table-text"
    //           checked={multiPoPost?.poItems && (multiPoPost.poItems[item.id] ?? 0)}
    //           onClick={(e) => e.stopPropagation()}
    //           onChange={(e) => {
    //             toggleSelect(item.id)
    //           }}
    //         />
    //       )
    //     }},
    //   ] : []
    // ),
    { label: "PO #", key: "document_identity" },
    { label: "PO Date", render: (item) => <Moment format="LL">{item.document_date}</Moment> },
    { label: "Delivery Date", render: (item) => item.delivery_date ? <Moment format="LL">{item.delivery_date}</Moment> : "" },
    { label: "Branch", key: "branch_name" },
    { label: "Supplier/Vendor", key: "supplier_name" },
    { label: "Ref Document No", key: "ref_document_identity" },
    {
      label: "Total", render: (item) => (
        <>
          {currencySymbolLeft()}
          {formatPrice(item.total_amount)}
          {currencySymbolRight()}
        </>
      )
    },
    {
      label: "Action", render: (item) => (
        <div className="d-flex justify-content-center">
          <button
            className="btn btn-secondary ml-2 rounded-sm del-btn"
            onClick={() => handleGetPrintData(item.id)}
          >
            <i className="fa fa-print edit-icon"></i>
          </button>
          {item.is_post && item.status === "Open" && checkPermission(authUserInfo.permissions, "PO Close") ? (
            <button
              title="Po Close"
              className="btn btn-warning ml-2 rounded-sm del-btn"
              onClick={() => handleClosingPo(item.id)}
            >
              <BiRefresh className="del-icon" />
            </button>
          ) : ""
          }
          {
            item.is_post !== 1 ? <>
              {
                item.status === "Pending" && (
                  <>
                    <NavLink
                      title="Edit"
                      disabled={item.id === btnDisable}
                      className="btn btn-primary ml-2 rounded-sm"
                      to={`/dashboard/manage/stock/purchase-order-edit/${item.id}`}
                      style={{ height: "1.7rem", width: "1.7rem" }}
                    >
                      <div className="d-flex align-items-center justify-content-center">
                        <i className="fa fa-pencil edit-icon" aria-hidden="true"></i>
                      </div>
                    </NavLink>
                    <button
                      title="Delete"
                      disabled={item.id === btnDisable}
                      className="btn btn-danger ml-2 rounded-sm del-btn"
                      onClick={() => handleDeleteConfirmation(item.id)}
                    >
                      <MdDelete className="del-icon" />
                    </button>
                  </>
                )
              }
              {item.status === "Pending" ? (
                (canPoApprove) && (
                  <button
                    title="Approve"
                    disabled={item.id === btnDisable}
                    className="btn btn-warning ml-2 rounded-sm del-btn"
                    onClick={() => handleApprove(item.id)}
                  >
                    <FaCheckCircle className="del-icon" />
                  </button>
                )) : (
                  canPoPost && (
                    <button
                      className="btn btn-dark ml-2 rounded-sm del-btn"
                      title="Post purchase"
                      disabled={loading || item.id === btnDisable}
                      style={{ padding: "1px 0px 0px 1px" }}
                      onClick={() => handlePostApi(item.id)}
                    >
                      <i className="fa fa-check-square-o" aria-hidden="true"></i>
                    </button>
                  )
                )
              }
            </> : ""
          }
        </div>
      )
    },
    { label: "Remarks", key: "remarks" },
    {
      label: "Status", render: (item) => (
        item.status === "Open" ? <span className="text-success">{item.status}</span> : (item.status === "Closed" ? <span className="text-danger">{item.status}</span> : <>{item.status}</>)
      )
    },
    {
      label: "Created By", render: (item) => <>{item?.created_by_name || "-"}</>
    },
    {
      label: "Updated By", render: (item) => <>{item?.updated_by_name || "-"}</>
    },
    {
      label: "Posted By", render: (item) => <>{item?.post_by_name || "-"}</>
    },
  ];

  const csvData = [
    {
      key: "id",
      index: 0,
    },
    {
      key: "delivery_date",
      index: 1,
    },
    {
      key: "branch_name",
      index: 2,
    },
    {
      key: "supplier_name",
      index: 3,
    },
    {
      key: "ref_document_identity",
      index: 4,
    },
    {
      key: "item_name",
      index: 5,
    },
    {
      key: "po_qty",
      index: 6,
    },
    {
      key: "unit",
      index: 7,
    },
    {
      key: "rate",
      index: 8,
    },
    {
      key: "remarks",
      index: 9,
    }
  ]

  return (
    <>
      <Helmet>
        <title>{_t(t("Purchases Order"))}</title>
      </Helmet>

      {/* main body */}
      <main id="main" data-simplebar>
        <div className="container-fluid">
          <div className="row t-mt-10 gx-2">
            {/* left Sidebar */}
            <div className="col-lg-2 col-xxl-2 t-mb-30 mb-lg-0">
              <ManageSidebar />
            </div>
            {/* left Sidebar ends */}

            {/* Rightbar contents */}
            <div className="col-lg-10 col-xxl-10 t-mb-30 mb-lg-0">
              <div className="t-bg-white">
                <div className=" row mx-3 gx-2 align-items-center t-pt-15 t-pb-15">
                  <div className="col-md-8 col-lg-7 t-mb-15 mb-md-0 d-flex justify-content-between align-items-center">
                    <ul className="t-list fk-breadcrumb">
                      <li className="fk-breadcrumb__list">
                        <span className="t-link fk-breadcrumb__link text-capitalize">
                          {t("Purchases Order List")}
                        </span>
                      </li>
                    </ul>
                    <div className="col-2">
                      {
                        canPoApprove || canPoPost ? (
                          <Select
                            options={PoTypes ? PoTypes : []}
                            getOptionLabel={(option) => option.name}
                            getOptionValue={(option) => option.name}
                            classNamePrefix="select"
                            styles={customStyle}
                            className="xsm-text"
                            value={multiPoPost.type}
                            onChange={handleSetType}
                            maxMenuHeight="200px"
                            placeholder={_t(t("Select type")) + ".."}
                          />
                        ) : ""
                      }
                    </div>
                    <CsvImportExport
                      filename="PO Sample Format CSV"
                      csvLength={10}
                      exportUrl="/settings/export-purchase-order-csv"
                      importUrl="/settings/upload-purchase-order-csv"
                      exportBtnName="Sample Format CSV"
                      csvData={csvData}
                      onCsvResponse={handleSetCsvResponse}
                    />
                  </div>
                  <div className="col-md-4 col-lg-5">
                    <div className="row gx-3 align-items-center">
                      {/* Search customer */}
                      <div className="col-md-8 t-mb-15 mb-md-0">
                        <div className="input-group">
                          <div className="form-file">
                            <input
                              ref={searchRef}
                              type="text"
                              className="form-control border-0 form-control--light-1 rounded-left"
                              placeholder={_t(t("Search")) + ".."}
                              onChange={handleSearch}
                              disabled={loading || dataPaginating}
                            />
                          </div>
                          <button
                            className="btn btn-primary rounded-right"
                            type="button"
                          >
                            <i
                              className="fa fa-search"
                              aria-hidden="true"
                            ></i>
                          </button>
                        </div>
                      </div>

                      {/* Add customer modal trigger button */}
                      <div className="col-md-4 text-md-right">
                        <NavLink
                          to="/dashboard/manage/stock/purchase-order-add"
                          className="btn btn-primary xsm-text text-uppercase btn-lg btn-block rounded"
                        >
                          {_t(t("add new"))}
                        </NavLink>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <DataTable
                loading={loading || dataPaginating}
                data={getListData()}
                columns={COLUMNS}
                emptyText="No data available"
                pagination={{
                  isPageLoading: loading,
                  isPagination: true,
                  pageCount: purchaseOrderList?.last_page,
                  currentPage: purchaseOrderList?.current_page,
                  from: purchaseOrderList?.from,
                  to: purchaseOrderList?.to,
                  total: purchaseOrderList?.total,
                }}
                handleFetchData={handleFetchData}
                searchPayload={{
                  isSearched: searchedSupplier.searched,
                  onClearSearch
                }}

              />
            </div>
            {/* Rightbar contents end*/}
          </div>

        </div>
      </main>
      {/* main body ends */}
      {/* print */}
      {puchaseOrderView && (
        <StockItemPrint
          name="Purchase Order"
          printDetailView={puchaseOrderView}
          columns={printColumns}
          data={newRow}
          totals={puchaseOrderView}
          showTotals={true}
          showSignatures={true}
          className="print-text-stock"
          PrintView={componentRef}
        />
      )}
    </>
  )
}

export default PurchaseOrderHistory
